// src/features/shared-scope/components/ShroomyStyleLab.tsx
// ────────────────────────────────────────────────────────────────────────────
// Shroomy Style Lab — Isolated neon theme prototype & reference
//
// Every UI primitive (titles, body text, buttons, badges, tabs, tables,
// inputs, cards) rendered in a single scrollable view so you can preview
// and tweak all neon color tokens before applying globally.
//
// Scoped: .shroomy-neon wrapper — does NOT leak to other pages.
// ────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import './shroomy-neon.css';

// ── Default Token Palette ────────────────────────────────────────────────

const DEFAULT_TOKENS: Record<string, string> = {
  // Backgrounds
  bgAbyss:     '#030005',
  bgCard:      '#050109',
  bgSurface:   '#27003d',

  // Borders
  borderBase:  '#1A0A2E',
  borderHover: '#2A1545',

  // Neon accents
  neonGreen:   '#39FF14',
  neonOrange:  '#ff0088',
  neonYellow:  '#ffd900',
  neonCyan:    '#f1b4fe',
  neonPink:    '#FF3366',
  neonViolet:  '#fb00ff',

  // Purple text scale
  textBright:  '#ff0088',
  textHeader:  '#9900ff',
  textBody:    '#ad42ff',
  textSoft:    '#cfa9fe',
  textMuted:   '#851aff',
  textDim:     '#8000ff',
  textGhost:   '#43256a',
};

// ── Token Editor (right column) ──────────────────────────────────────────

function TokenEditor({
  tokens,
  onChange,
  onReset,
}: {
  tokens: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onReset: () => void;
}) {
  const groups = [
    { label: 'Backgrounds', keys: ['bgAbyss', 'bgCard', 'bgSurface'] },
    { label: 'Borders', keys: ['borderBase', 'borderHover'] },
    { label: 'Neon Accents', keys: ['neonGreen', 'neonOrange', 'neonYellow', 'neonCyan', 'neonPink', 'neonViolet'] },
    { label: 'Purple Text Scale', keys: ['textBright', 'textHeader', 'textBody', 'textSoft', 'textMuted', 'textDim', 'textGhost'] },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: tokens.textHeader }}>
          Token Tweaker
        </h3>
        <button
          onClick={onReset}
          className="text-[10px] px-2 py-0.5 rounded border transition-colors"
          style={{ color: tokens.neonPink, borderColor: `${tokens.neonPink}33`, background: 'transparent' }}
        >
          Reset All
        </button>
      </div>

      {groups.map(g => (
        <div key={g.label}>
          <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: tokens.textDim }}>{g.label}</div>
          <div className="space-y-1">
            {g.keys.map(k => (
              <div key={k} className="flex items-center gap-2">
                <input
                  type="color"
                  value={tokens[k]}
                  onChange={e => onChange(k, e.target.value)}
                  className="w-5 h-5 rounded border-0 cursor-pointer bg-transparent p-0"
                />
                <span className="text-[10px] font-mono flex-1" style={{ color: tokens.textMuted }}>{k}</span>
                <input
                  type="text"
                  value={tokens[k]}
                  onChange={e => onChange(k, e.target.value)}
                  className="w-[5.5rem] text-[10px] font-mono px-1.5 py-0.5 rounded border outline-none"
                  style={{
                    color: tokens.textBody,
                    borderColor: tokens.borderBase,
                    background: tokens.bgCard,
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Lab ─────────────────────────────────────────────────────────────

export default function ShroomyStyleLab() {
  const [tokens, setTokens] = useState<Record<string, string>>({ ...DEFAULT_TOKENS });
  const [activeDemo, setActiveDemo] = useState('Overview');

  const t = tokens; // shorthand

  const update = (key: string, value: string) => {
    setTokens(prev => ({ ...prev, [key]: value }));
  };

  const DEMO_TABS = ['Overview', 'Typography', 'Buttons', 'Table', 'Cards'];

  return (
    <div className="shroomy-neon h-full min-h-0 flex" style={{ background: t.bgAbyss }}>
      {/* ── Left: Live Preview ────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-6" style={{ borderRight: `1px solid ${t.borderBase}` }}>

        {/* Lab Tabs */}
        <div className="flex gap-0.5 border-b" style={{ borderColor: t.borderBase }}>
          {DEMO_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveDemo(tab)}
              className="px-3 py-1.5 text-xs font-medium border-b-2 transition-colors"
              style={{
                color: activeDemo === tab ? t.neonViolet : t.textDim,
                borderColor: activeDemo === tab ? t.neonViolet : 'transparent',
                background: 'transparent',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* ── Overview ──────────────────────────────────────────── */}
        {activeDemo === 'Overview' && (
          <div className="space-y-6">
            {/* Color swatches */}
            <section>
              <h2 className="text-sm font-semibold mb-3" style={{ color: t.textHeader }}>Neon Accent Palette</h2>
              <div className="flex gap-3 flex-wrap">
                {(['neonGreen', 'neonOrange', 'neonYellow', 'neonCyan', 'neonPink', 'neonViolet'] as const).map(k => (
                  <div key={k} className="flex flex-col items-center gap-1">
                    <div
                      className="w-10 h-10 rounded-lg border"
                      style={{ background: t[k], borderColor: `${t[k]}40`, boxShadow: `0 0 12px ${t[k]}30` }}
                    />
                    <span className="text-[9px] font-mono" style={{ color: t.textDim }}>{k.replace('neon', '')}</span>
                    <span className="text-[9px] font-mono" style={{ color: t.textGhost }}>{t[k]}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Background layers */}
            <section>
              <h2 className="text-sm font-semibold mb-3" style={{ color: t.textHeader }}>Background Depth</h2>
              <div className="flex gap-3">
                {(['bgAbyss', 'bgCard', 'bgSurface'] as const).map(k => (
                  <div
                    key={k}
                    className="w-24 h-16 rounded-lg border flex items-center justify-center"
                    style={{ background: t[k], borderColor: t.borderBase }}
                  >
                    <span className="text-[9px] font-mono" style={{ color: t.textDim }}>{k}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Status badges */}
            <section>
              <h2 className="text-sm font-semibold mb-3" style={{ color: t.textHeader }}>Status Badges</h2>
              <div className="flex gap-2">
                {([
                  { label: 'ACTIVE', color: t.neonGreen },
                  { label: 'EXPIRED', color: t.neonYellow },
                  { label: 'REVOKED', color: t.neonPink },
                  { label: 'PENDING', color: t.neonCyan },
                  { label: 'DEFAULT', color: t.neonViolet },
                ]).map(b => (
                  <span
                    key={b.label}
                    className="text-[10px] px-2 py-0.5 font-medium"
                    style={{ color: b.color }}
                  >
                    {b.label}
                  </span>
                ))}
              </div>
            </section>
          </div>
        )}

        {/* ── Typography ────────────────────────────────────────── */}
        {activeDemo === 'Typography' && (
          <div className="space-y-5">
            <section>
              <h2 className="text-sm font-semibold mb-3" style={{ color: t.textHeader }}>Text Scale</h2>
              <div className="space-y-2 rounded-lg p-4 border" style={{ background: t.bgCard, borderColor: t.borderBase }}>
                <div className="text-xl font-bold" style={{ color: t.neonOrange }}>Bold Neon Title — Orange</div>
                <div className="text-lg font-semibold" style={{ color: t.textBright }}>Section Header — Bright Purple</div>
                <div className="text-sm font-medium" style={{ color: t.textHeader }}>Subsection — Header Violet</div>
                <div className="text-sm" style={{ color: t.textBody }}>Body text renders in soft purple. Clean readability on near-black backgrounds.</div>
                <div className="text-xs" style={{ color: t.textSoft }}>Secondary text — softer purple for metadata and descriptions.</div>
                <div className="text-[10px] uppercase tracking-wider" style={{ color: t.textMuted }}>Label / Muted — uppercase tracking</div>
                <div className="text-[10px]" style={{ color: t.textDim }}>Dim helper text for tertiary information.</div>
                <div className="text-[10px]" style={{ color: t.textGhost }}>Ghost text — barely visible, placeholder-level.</div>
              </div>
            </section>

            <section>
              <h2 className="text-sm font-semibold mb-3" style={{ color: t.textHeader }}>Neon Title Variants</h2>
              <div className="space-y-1 rounded-lg p-4 border" style={{ background: t.bgCard, borderColor: t.borderBase }}>
                <div className="text-lg font-bold" style={{ color: t.neonGreen }}>Green Neon — Active / Success</div>
                <div className="text-lg font-bold" style={{ color: t.neonOrange }}>Orange Neon — Primary Entity Title</div>
                <div className="text-lg font-bold" style={{ color: t.neonYellow }}>Yellow Neon — Warning / Expires</div>
                <div className="text-lg font-bold" style={{ color: t.neonCyan }}>Cyan Neon — Data / Scope Keys</div>
                <div className="text-lg font-bold" style={{ color: t.neonPink }}>Pink Neon — Danger / Revoked</div>
                <div className="text-lg font-bold" style={{ color: t.neonViolet }}>Violet Neon — Brand / Accent</div>
              </div>
            </section>
          </div>
        )}

        {/* ── Buttons ───────────────────────────────────────────── */}
        {activeDemo === 'Buttons' && (
          <div className="space-y-5">
            <section>
              <h2 className="text-sm font-semibold mb-3" style={{ color: t.textHeader }}>Border-Only Buttons (No Background Fill)</h2>
              <div className="space-y-2">
                {([
                  { label: 'Primary Action',   color: t.neonOrange },
                  { label: 'Confirm / Create',  color: t.neonGreen },
                  { label: 'Warning / Caution', color: t.neonYellow },
                  { label: 'Danger / Revoke',   color: t.neonPink },
                  { label: 'Info / Navigate',    color: t.neonCyan },
                  { label: 'Default / Cancel',   color: t.neonViolet },
                ]).map(b => (
                  <button
                    key={b.label}
                    className="w-full px-4 py-2 rounded text-sm font-medium border transition-all
                               hover:shadow-[0_0_8px_var(--glow)]"
                    style={{
                      color: b.color,
                      borderColor: `${b.color}40`,
                      background: 'transparent',
                      ['--glow' as string]: `${b.color}30`,
                    }}
                    onMouseEnter={e => {
                      (e.target as HTMLElement).style.borderColor = `${b.color}80`;
                    }}
                    onMouseLeave={e => {
                      (e.target as HTMLElement).style.borderColor = `${b.color}40`;
                    }}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-sm font-semibold mb-3" style={{ color: t.textHeader }}>Small / Inline Buttons</h2>
              <div className="flex gap-2 flex-wrap">
                {([
                  { label: 'Copy', color: t.neonCyan },
                  { label: 'Edit', color: t.neonOrange },
                  { label: 'Delete', color: t.neonPink },
                  { label: 'View', color: t.neonViolet },
                ]).map(b => (
                  <button
                    key={b.label}
                    className="px-2.5 py-1 rounded text-[11px] font-medium border transition-colors"
                    style={{ color: b.color, borderColor: `${b.color}30`, background: 'transparent' }}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-sm font-semibold mb-3" style={{ color: t.textHeader }}>Form Inputs</h2>
              <div className="space-y-2 max-w-sm">
                <input
                  type="text"
                  placeholder="Search or filter…"
                  className="w-full h-8 px-3 rounded-md text-xs outline-none focus:ring-1 border"
                  style={{
                    background: t.bgCard,
                    borderColor: t.borderBase,
                    color: t.textBody,
                    ['--tw-ring-color' as string]: t.neonViolet,
                  }}
                />
                <select
                  className="w-full h-8 px-3 rounded-md text-xs outline-none border appearance-none"
                  style={{ background: t.bgCard, borderColor: t.borderBase, color: t.textBody }}
                >
                  <option>Option A</option>
                  <option>Option B</option>
                </select>
              </div>
            </section>
          </div>
        )}

        {/* ── Table ─────────────────────────────────────────────── */}
        {activeDemo === 'Table' && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold" style={{ color: t.textHeader }}>Data Table Preview</h2>
            <div className="rounded-lg border overflow-hidden" style={{ borderColor: t.borderBase }}>
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${t.borderBase}` }}>
                    {['Time', 'Status', 'Label', 'Scope', 'Tenant', 'Hits'].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-medium" style={{ color: t.textDim }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {([
                    { time: '2m ago', status: 'ACTIVE', statusColor: t.neonGreen, label: 'Final Fantasy — Big Brother View', scope: 'tableops:ff', tenant: '1', hits: '42' },
                    { time: '1h ago', status: 'EXPIRED', statusColor: t.neonYellow, label: 'IoT Temp Dashboard', scope: 'iot:sensors', tenant: '1', hits: '18' },
                    { time: '3d ago', status: 'REVOKED', statusColor: t.neonPink, label: 'Debug Viewer', scope: 'generic:debug', tenant: '2', hits: '7' },
                    { time: '5m ago', status: 'ACTIVE', statusColor: t.neonGreen, label: 'Kitchen Monitor — Live Orders', scope: 'tableops:kitchen', tenant: '1', hits: '156' },
                  ]).map((row, i) => (
                    <tr
                      key={i}
                      className="transition-colors cursor-pointer"
                      style={{ borderBottom: `1px solid ${t.borderBase}` }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(64,0,149,0.10)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                    >
                      <td className="px-3 py-2" style={{ color: t.textSoft }}>{row.time}</td>
                      <td className="px-3 py-2">
                        <span
                          className="text-[10px] px-1.5 py-0.5 font-medium"
                          style={{ color: row.statusColor }}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-semibold" style={{ color: t.neonOrange }}>{row.label}</td>
                      <td className="px-3 py-2 font-mono text-[10px]" style={{ color: t.neonCyan }}>{row.scope}</td>
                      <td className="px-3 py-2" style={{ color: t.neonYellow }}>{row.tenant}</td>
                      <td className="px-3 py-2 text-right" style={{ color: t.textSoft }}>{row.hits}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Cards ─────────────────────────────────────────────── */}
        {activeDemo === 'Cards' && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold" style={{ color: t.textHeader }}>Card Components</h2>
            <div className="grid grid-cols-2 gap-3">
              {([
                { title: 'Active Links', value: '12', accent: t.neonGreen },
                { title: 'Expired', value: '3', accent: t.neonYellow },
                { title: 'Revoked', value: '1', accent: t.neonPink },
                { title: 'Total Hits', value: '2.4k', accent: t.neonCyan },
              ]).map(c => (
                <div
                  key={c.title}
                  className="rounded-lg border p-3"
                  style={{ background: t.bgCard, borderColor: t.borderBase }}
                >
                  <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: t.textDim }}>{c.title}</div>
                  <div className="text-2xl font-bold" style={{ color: c.accent }}>{c.value}</div>
                </div>
              ))}
            </div>

            {/* Detail card */}
            <div className="rounded-lg border p-4 space-y-2" style={{ background: t.bgCard, borderColor: t.borderBase }}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold" style={{ color: t.neonOrange }}>Entity Detail Card</span>
                <span
                  className="text-[10px] px-1.5 py-0.5 font-medium"
                  style={{ color: t.neonGreen }}
                >
                  ACTIVE
                </span>
              </div>
              {([
                { label: 'Scope Type', value: 'table-ops' },
                { label: 'Scope', value: 'tableops:finalfantasy' },
                { label: 'Tenant ID', value: '1' },
                { label: 'Permissions', value: 'read' },
                { label: 'Created By', value: 'system:seed' },
              ]).map(row => (
                <div key={row.label} className="flex justify-between items-center py-1 border-b" style={{ borderColor: `${t.borderBase}80` }}>
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: t.textDim }}>{row.label}</span>
                  <span className="text-xs" style={{ color: t.textBody }}>{row.value}</span>
                </div>
              ))}
              <div className="pt-2 space-y-1.5">
                <button
                  className="w-full px-3 py-1.5 rounded text-xs font-medium border transition-colors"
                  style={{ color: t.neonOrange, borderColor: `${t.neonOrange}40`, background: 'transparent' }}
                >
                  Recreate Link
                </button>
                <button
                  className="w-full px-3 py-1.5 rounded text-xs font-medium border transition-colors"
                  style={{ color: t.neonViolet, borderColor: `${t.neonViolet}30`, background: 'transparent' }}
                >
                  Revoke Link
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Right: Token Tweaker Panel ─────────────────────────── */}
      <div
        className="w-[260px] shrink-0 overflow-y-auto p-4"
        style={{ background: t.bgCard, borderLeft: `1px solid ${t.borderBase}` }}
      >
        <TokenEditor tokens={tokens} onChange={update} onReset={() => setTokens({ ...DEFAULT_TOKENS })} />
      </div>
    </div>
  );
}
